// =============================================================================
// useMapPersona — persona / cheat-sheet per mapa (imię, serwis, social, notatki).
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { MapPersona, PersonaSocialLink } from "@/types/uniquePages";

export function useMapPersona(mapId: string | undefined) {
  const [persona, setPersona] = useState<MapPersona | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!mapId) return;
    setLoading(true);
    try {
      const { data } = await (supabase as any)
        .from("map_persona")
        .select("*")
        .eq("map_id", mapId)
        .maybeSingle();
      setPersona(data ?? null);
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(
    async (fields: {
      full_name?: string;
      service_label?: string;
      social_links?: PersonaSocialLink[];
      notes?: string;
      /** null = wyczyść prompt (dlatego sprawdzamy obecność klucza, nie ?? — null musi przejść). */
      author_photo_prompt?: string | null;
    }) => {
      if (!mapId) return;
      setSaving(true);
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
        const row = {
          map_id: mapId,
          user_id: userId,
          full_name: fields.full_name ?? persona?.full_name ?? null,
          service_label: fields.service_label ?? persona?.service_label ?? null,
          social_links: fields.social_links ?? persona?.social_links ?? [],
          notes: fields.notes ?? persona?.notes ?? null,
          author_photo_prompt:
            "author_photo_prompt" in fields
              ? fields.author_photo_prompt ?? null
              : persona?.author_photo_prompt ?? null,
          updated_at: new Date().toISOString(),
        };
        await (supabase as any).from("map_persona").upsert(row, { onConflict: "map_id" });
        await load();
      } finally {
        setSaving(false);
      }
    },
    [mapId, persona, load],
  );

  return { persona, loading, saving, save, refresh: load };
}
